import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import {
  Button,
  ButtonAppearance,
  Icon,
  LearningOverviewCard,
  Eyebrow,
  DropdownMenu,
} from '@atlassian/learning-components';
import { useNavigate, useSearchParams, useLoaderData } from 'react-router-dom';

import { MyLearningLoaderProps } from 'src/pages/MyLearningPage/loaderV2';
import { MyLearningResourceData } from 'src/pages/MyLearningPage';
import { MobileFilterMenu } from 'src/components/MobileFilterMenu';

import {
  useMyLearningFilters,
  useMyLearningData,
  useFilteredAndSortedItems,
  useMyLearningCardActions,
  useLearningCardProps,
  QUERY_PARAM_KEYS,
  FILTER_SECTION_TITLES,
} from './hooks';
import { EMPTY_STATE_TYPES, EmptyStateType, LearningEmptyState } from './LearningEmptyStateV2';

export const MyLearningOnDemandContent = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loaderData = useLoaderData() as MyLearningLoaderProps;
  const [isFullTranscriptDataLoading, setIsFullTranscriptDataLoading] = useState(false);
  const [learningData, setLearningData] = useState<MyLearningResourceData | null>(loaderData.myLearningResourceData);

  // Sync state with loader data when it changes
  useEffect(() => {
    setLearningData(loaderData.myLearningResourceData);
  }, [loaderData.myLearningResourceData]);

  // Data transformation and combination
  const allItems = useMyLearningData(
    loaderData.recentlyViewedLessonsResources,
    learningData,
    loaderData.archivedContentResources,
  );

  const filters = useMyLearningFilters(allItems);

  const filteredAndSortedItems = useFilteredAndSortedItems(allItems, {
    selectedStatus: filters.selectedStatus,
    selectedType: filters.selectedType,
    selectedApps: filters.selectedApps,
    selectedDuration: filters.selectedDuration,
    currentSort: filters.currentSort,
  });

  const cardActions = useMyLearningCardActions({
    learningData,
    setLearningData,
    loaderData,
    setIsFullTranscriptDataLoading,
  });

  const cardProps = useLearningCardProps(filteredAndSortedItems, {
    handleTitleClick: cardActions.handleTitleClick,
    handleViewUpdates: cardActions.handleViewUpdates,
    handleGeneratePdf: cardActions.handleGeneratePdf,
    handleSave: cardActions.handleSave,
  });

  // Handle applying filters and sort from mobile filter menu
  const handleApplyFilters = useCallback(
    (filters: Record<string, string[]>, sortValue?: string): void => {
      const newSearchParams = new URLSearchParams(searchParams);

      // Clear existing filter params
      Object.keys(filters).forEach((key) => {
        newSearchParams.delete(key);
      });

      // Apply new filters
      Object.entries(filters).forEach(([key, values]) => {
        if (values.length > 0) {
          newSearchParams.set(key, values.join(','));
        }
      });

      // Apply sort if provided
      if (sortValue) {
        newSearchParams.set(QUERY_PARAM_KEYS.SORT, sortValue);
      }

      // Single navigation with both filters and sort
      navigate({ search: newSearchParams.toString() });
    },
    [searchParams, navigate],
  );

  const hasItems = allItems.length > 0;
  const hasFilteredItems = cardProps.length > 0;
  const emptyStateType: EmptyStateType =
    hasItems && !hasFilteredItems
      ? EMPTY_STATE_TYPES.ON_DEMAND_EMPTY_FILTER
      : EMPTY_STATE_TYPES.ON_DEMAND_EMPTY_RESULTS;

  return (
    <div css={containerStyles}>
      {/* Mobile Filter and Sort - shown on mobile only */}
      <div css={mobileFilterContainerStyles}>
        <MobileFilterMenu
          filterSections={filters.mobileFilterSections}
          selectedFiltersLength={filters.selectedFiltersLength}
          onApplyFilters={handleApplyFilters}
          onClearAll={filters.clearAllFilters}
          sortConfig={filters.mobileSortConfig}
          auxiliaryComponent={
            <Button
              data-testid="browse-catalog-button"
              label="Browse catalog"
              appearance={ButtonAppearance.PRIMARY}
              onClick={cardActions.handleBrowseCatalogClick}
            />
          }
        />
      </div>

      {/* Desktop Controls Row - shown on desktop only */}
      <div css={[controlsRowStyles, desktopOnlyStyles]}>
        {/* Left side: Filters and Sort */}
        {hasItems && (
          <div css={leftControlsStyles}>
            <div css={{ display: 'flex', flexDirection: 'row', gap: token('space.150'), alignItems: 'center' }}>
              <Eyebrow size="medium" fontWeight="semibold">
                {FILTER_SECTION_TITLES.SORT_BY}
              </Eyebrow>
              <DropdownMenu
                data-testid="sort-dropdown"
                items={filters.sortItems}
                onItemClick={filters.handleSortChange}
                placement="bottom-start"
                isCheckboxMenu={false}
              />
            </div>

            <div css={{ display: 'flex', flexDirection: 'row', gap: token('space.150'), alignItems: 'center' }}>
              <Eyebrow size="medium" fontWeight="semibold">
                {FILTER_SECTION_TITLES.FILTER_BY}
              </Eyebrow>
              <DropdownMenu
                data-testid="status-filter-dropdown"
                items={filters.statusItems}
                onItemClick={(label) =>
                  filters.handleFilterChange(QUERY_PARAM_KEYS.STATUS, label, filters.selectedStatus.includes(label))
                }
                placement="bottom-start"
                isCheckboxMenu={true}
                triggerText={FILTER_SECTION_TITLES.STATUS}
              />

              <DropdownMenu
                data-testid="type-filter-dropdown"
                items={filters.typeItems}
                onItemClick={(label) =>
                  filters.handleFilterChange(QUERY_PARAM_KEYS.TYPE, label, filters.selectedType.includes(label))
                }
                placement="bottom-start"
                isCheckboxMenu={true}
                triggerText={FILTER_SECTION_TITLES.TYPE}
              />

              <DropdownMenu
                data-testid="apps-filter-dropdown"
                items={filters.appsItems}
                onItemClick={(label) =>
                  filters.handleFilterChange(QUERY_PARAM_KEYS.APPS, label, filters.selectedApps.includes(label))
                }
                placement="bottom-start"
                isCheckboxMenu={true}
                triggerText={FILTER_SECTION_TITLES.APP}
              />

              <DropdownMenu
                data-testid="duration-filter-dropdown"
                items={filters.durationItems}
                onItemClick={(label) =>
                  filters.handleFilterChange(QUERY_PARAM_KEYS.DURATION, label, filters.selectedDuration.includes(label))
                }
                placement="bottom-start"
                isCheckboxMenu={true}
                triggerText={FILTER_SECTION_TITLES.DURATION}
              />
              {filters.hasActiveFilters && (
                <Button
                  data-testid="clear-filters-button"
                  label="Clear filters"
                  appearance={ButtonAppearance.LINK}
                  onClick={filters.clearAllFilters}
                />
              )}
            </div>
          </div>
        )}

        {/* Right side: Action Buttons */}
        <div css={rightControlsStyles}>
          <Button
            data-testid="learning-history-button"
            label="Learning history"
            appearance={ButtonAppearance.SECONDARY}
            iconBefore={<Icon type="download-core" color={token('color.icon.brand')} />}
            onClick={cardActions.handleLearningHistoryClick}
            isLoading={isFullTranscriptDataLoading}
          />
          <Button
            data-testid="browse-catalog-button"
            label="Browse catalog"
            appearance={ButtonAppearance.PRIMARY}
            onClick={cardActions.handleBrowseCatalogClick}
          />
        </div>
      </div>

      {/* Content Area */}
      <div css={contentAreaStyles}>
        {hasFilteredItems ? (
          <div css={cardsListStyles}>
            {cardProps.map((props, index) => (
              <LearningOverviewCard key={`${props.course.slug}-${index}`} {...props} />
            ))}
          </div>
        ) : (
          <LearningEmptyState emptyStateType={emptyStateType} />
        )}
      </div>
    </div>
  );
};

const containerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.300'),
});

const controlsRowStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: token('space.200'),
  paddingTop: token('space.400'),
});

const leftControlsStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: token('space.250'),

  [media.below.sm]: {
    width: '100%',
  },

  // override eyebrow margin
  '& h4': {
    margin: 0,
  },
});

const rightControlsStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.150'),
  marginLeft: 'auto',

  [media.below.sm]: {
    width: '100%',
    justifyContent: 'flex-end',
  },

  // override download-core icon color, since color prop is not working
  '& a svg *': {
    stroke: token('color.icon.brand'),
  },

  '& a:hover svg *': {
    stroke: token('color.icon.inverse'),
  },
});

const contentAreaStyles = css({
  width: '100%',
  paddingTop: token('space.250'),
});

const mobileFilterContainerStyles = css({
  display: 'none',

  [media.below.md]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${token('space.300')} 0`,
    borderBottom: `1px solid ${token('color.border')}`,
  },
});

const desktopOnlyStyles = css({
  display: 'flex',

  [media.below.md]: {
    display: 'none',
  },
});

const cardsListStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  width: '100%',
});
